import { ControlledModal, DropzoneInput, toast } from "@/components"
import { cache } from "@/gql-clients/experimental/cache"
import { CoverImageWithEditFragmentDoc, CreateJobPostingFileDocument, JobPostingFileType } from "@/gql/graphql"
import { useMutation } from "@apollo/client"
import { captureException } from "@sentry/nextjs"
import axios from "axios"
import { useState } from "react"

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  jobPostId: string
  jobPostTypename: "ClientJobPosting" | "CreativeJobPosting"
}

export const AddCoverImageModal: React.FC<Props> = ({ open, onOpenChange, jobPostId, jobPostTypename }) => {
  const [image, setImage] = useState<{
    file: null | File
    imagePreview: null | string
  }>({
    file: null,
    imagePreview: null,
  })
  const [loading, setLoading] = useState(false)
  const [createJobPostFile] = useMutation(CreateJobPostingFileDocument)

  const uploadImageFile = async ({ file, fileName }: { file: string; fileName: string }) => {
    let createFileResponse
    try {
      createFileResponse = await createJobPostFile({
        variables: {
          jobPostingId: jobPostId,
          originalFileName: fileName,
          type: JobPostingFileType.CoverImage,
        },
      })
    } catch (e) {
      console.error(e)
      return { s3Response: null, createFileResponseData: null }
    }

    if (createFileResponse?.errors) {
      console.error(createFileResponse.errors)
      toast.error("Error adding cover image")

      return { s3Response: null, createFileResponseData: null }
    }
    const createdFile = createFileResponse?.data?.createJobPostingFile?.createdFile
    const fields = JSON.parse(createdFile?.presignedPost?.fields)

    const blobResponse = await fetch(file)
    const blob = await blobResponse.blob()
    const contentType = blob.type || "application/octet-stream"

    const formData = new FormData()

    Object.entries(fields).forEach(([key, value]) => {
      formData.append(key, value as string)
    })

    formData.append("file", blob, fileName)
    formData.append("Content-Type", contentType)

    if (!createdFile?.presignedPost?.url) {
      toast.error("Error adding cover image")
      return { s3Response: null, createFileResponseData: null }
    }

    try {
      const s3Response = await axios.post(createdFile?.presignedPost?.url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return { s3Response, createFileResponseData: createFileResponse?.data?.createJobPostingFile }
    } catch (e) {
      captureException(e)
      toast.error("Error adding cover image")
      return { s3Response: null, createFileResponseData: null }
    }
  }

  const onSelectFile = (files: File[]) => {
    const reader = new FileReader()
    reader.addEventListener("load", () => {
      setImage({
        file: files[0],
        imagePreview: reader.result?.toString() || "",
      })
    })

    const file = files[0]

    reader.readAsDataURL(file)
  }

  const handleUpload = async () => {
    if (loading) return
    setLoading(true)
    const { createFileResponseData } = await uploadImageFile({
      file: image.imagePreview || "",
      fileName: image.file?.name || "",
    })

    const cacheId = cache.identify({
      __typename: jobPostTypename,
      id: jobPostId,
    })

    if (createFileResponseData) {
      cache.writeFragment({
        fragment: CoverImageWithEditFragmentDoc,
        id: cacheId,
        data: {
          id: jobPostId,
          __typename: jobPostTypename,
          coverImage: {
            id: createFileResponseData?.createdFile?.file?.id!,
            __typename: "JobPostingFile",
            thumbnailUrl645: image.imagePreview!,
          },
        },
      })

      onOpenChange(false)
    }
    setLoading(false)
  }

  return (
    <ControlledModal
      open={open}
      onOpenChange={onOpenChange}
      header={{ title: "Upload cover image", variant: "default" }}
      variant="default"
      zIndex={102}
      footer={{
        fixed: true,
        primaryButton: {
          children: "Upload",
          onClick: async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            e.stopPropagation()
            await handleUpload()
          },
          loading: loading,
        },
        secondaryButton: {
          children: "Cancel",
          onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            e.stopPropagation()
            onOpenChange(false)
          },
        },
      }}
      body={
        <div className="flex flex-1 flex-col justify-center overflow-y-scroll">
          <DropzoneInput
            onSelectFile={onSelectFile}
            imagePreview={image.imagePreview}
            onRemovePreview={() => {
              setImage({
                file: null,
                imagePreview: null,
              })
            }}
          />
        </div>
      }
    />
  )
}
